/**
 * Every tenant's add-ons (TMMPDM-0019): `GET /v1/admin/addons?status=` behind a status toggle
 * ("All" omits the param). Revenue this year is the server's per-row figure in that row's
 * currency — shown per row, never summed. TMMPDM-0035: the toggle lives in the URL (`status`);
 * a filtered empty list offers Clear filters.
 */
import { useEffect, useState } from 'react';
import {
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';
import { oneOf, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { EmptyState, ErrorState } from '@/components/page';
import { Amount } from '@/components/ui';
import { adminApi } from '@/api/tmm/admin';
import type { TenantAddonStatus, TenantAddonView } from '@/api/tmm/types';
import { formatDate } from '@/format';
import {
  BILLING_MODE_FALLBACK,
  PER_PERIOD_FALLBACK,
  TENANT_ADDON_STATUSES,
  TENANT_ADDON_STATUS_FALLBACK,
} from './addonFormat';
import { StatusChip } from '@/status';

type StatusFilter = TenantAddonStatus | 'ALL';

/** TMMPDM-0035: the status toggle is the URL's `status` (default All, omitted). */
type FleetAddonsView = { status: StatusFilter };
const VIEW: UrlSchema<FleetAddonsView> = {
  status: { default: 'ALL', parse: oneOf<StatusFilter>(['ALL', ...TENANT_ADDON_STATUSES]) },
};

interface FleetAddonsTableProps {
  locale: string;
  /** Bumped by the page to re-read after a catalogue change. */
  refreshKey: number;
}

export function FleetAddonsTable({ locale, refreshKey }: FleetAddonsTableProps) {
  const { t } = useCopy();
  const [view, setView] = useUrlState(VIEW);
  const status = view.status;
  const [reloadToken, setReloadToken] = useState(0);
  const key = `${status}:${reloadToken}:${refreshKey}`;
  // A result belongs to the key it was loaded for; a stale one reads as loading.
  const [state, setState] = useState<{
    key: string;
    rows: TenantAddonView[] | null;
    error: { cause: unknown } | null;
  }>({ key: '', rows: null, error: null });

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listFleetAddons(status === 'ALL' ? undefined : status)
      .then((list) => {
        if (!cancelled) setState({ key, rows: list.addons, error: null });
      })
      .catch((err: unknown) => {
        if (!cancelled) setState({ key, rows: null, error: { cause: err } });
      });
    return () => {
      cancelled = true;
    };
  }, [key, status]);

  const current = state.key === key;
  const rows = current ? state.rows : null;
  const error = current ? state.error : null;

  return (
    <Stack spacing={2}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={status}
        onChange={(_event, value: StatusFilter | null) => {
          if (value) setView({ status: value });
        }}
        aria-label={t('addons.fleet.statusAria', 'Add-on status')}
        sx={{ flexWrap: 'wrap' }}
      >
        <ToggleButton value="ALL">{t('addons.fleet.all', 'All')}</ToggleButton>
        {TENANT_ADDON_STATUSES.map((value) => (
          <ToggleButton key={value} value={value}>
            {t(`addons.status.${value}`, TENANT_ADDON_STATUS_FALLBACK[value])}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {error ? (
        <ErrorState
          error={error.cause}
          message={t('addons.fleet.loadError', "Could not load tenants' add-ons.")}
          onRetry={() => setReloadToken((n) => n + 1)}
        />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label={t('addons.fleet.tableAria', 'Tenant add-ons')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('addons.col.tenant', 'Tenant')}</TableCell>
                <TableCell>{t('addons.col.addon', 'Add-on')}</TableCell>
                <TableCell>{t('addons.col.status', 'Status')}</TableCell>
                <TableCell>{t('addons.col.billingMode', 'Billing mode')}</TableCell>
                <TableCell>{t('addons.col.price', 'Price per period')}</TableCell>
                <TableCell>{t('addons.col.term', 'Term')}</TableCell>
                <TableCell>{t('addons.col.autoRenew', 'Auto-renew')}</TableCell>
                <TableCell>{t('addons.col.revenueYear', 'Revenue this year')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows === null ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    {t('common.loading', 'Loading')}
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <EmptyState
                      title={t('addons.fleet.empty', 'No tenant add-ons.')}
                      {...(status === 'ALL'
                        ? {}
                        : { filtered: { onClear: () => setView({ status: 'ALL' }) } })}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      {row.tenantSlug ? (
                        <Link
                          component={RouterLink}
                          to={`/partners/by-tenant/${encodeURIComponent(row.tenantSlug)}?tab=addons`}
                        >
                          {row.tenantSlug}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>
                      <StatusChip
                        status={row.status}
                        label={t(
                          `addons.status.${row.status}`,
                          TENANT_ADDON_STATUS_FALLBACK[row.status],
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      {row.billingMode
                        ? t(
                            `addons.billingMode.${row.billingMode}`,
                            BILLING_MODE_FALLBACK[row.billingMode],
                          )
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Amount minor={row.priceMinor} currency={row.currency} locale={locale} />{' '}
                      {t(`addons.perPeriod.${row.period}`, PER_PERIOD_FALLBACK[row.period])}
                    </TableCell>
                    <TableCell>
                      {row.termStart || row.termEnd
                        ? `${formatDate(row.termStart, locale)} – ${formatDate(row.termEnd, locale)}`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {row.autoRenew ? t('addons.yes', 'Yes') : t('addons.no', 'No')}
                    </TableCell>
                    <TableCell>
                      <Amount
                        minor={row.revenueYearMinor}
                        currency={row.currency}
                        locale={locale}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}
